import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  LifeBuoy, Mail, ExternalLink, Copy, Check, Ticket, BookOpen,
  ShieldAlert, ClipboardList,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';

/** Everything a support agent asks for, gathered before they have to ask.
 *
 *  A ticket that says "it does not work" costs two round trips to become
 *  actionable: which workspace, which role, when, and what the browser was.
 *  That is all knowable here, so it is collected into one copyable block
 *  rather than requested afterwards.
 */
const useDiagnostics = () => {
  const { user } = useAuth();
  const { appName } = useBranding();
  return useMemo(() => {
    const rows = [
      ['Workspace', user?.tenantName ? `${user.tenantName} (${user.tenantSlug || '—'})` : '—'],
      ['Role', user?.role || '—'],
      ['Signed in as', user?.email || '—'],
      ['Employee ID', user?.employeeId || '—'],
      ['Plan', user?.subscriptionPlanName || user?.subscriptionPlanCode || '—'],
      ['Product', appName || 'WebHR'],
      ['Page', typeof window !== 'undefined' ? window.location.pathname : '—'],
      ['Browser', typeof navigator !== 'undefined' ? navigator.userAgent : '—'],
      ['Local time', new Date().toString()],
    ];
    // A proxy session changes whose account the agent is actually looking at,
    // which is the single most confusing thing to debug without being told.
    if (user?.isProxySession) {
      rows.splice(2, 0, ['Proxy session', `yes — from ${user.proxySourceTenantSlug || 'platform'}`]);
    }
    return rows;
  }, [user, appName]);
};

const Row = ({ label, value }) => (
  <div className="flex flex-col gap-0.5 border-b border-border/60 py-2 last:border-0 sm:flex-row sm:items-baseline sm:gap-3">
    <span className="w-40 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className="break-all text-sm">{value}</span>
  </div>
);

const SupportCenter = ({ onNavigate }) => {
  const { user } = useAuth();
  const { branding, appName } = useBranding();
  const diagnostics = useDiagnostics();
  const [copied, setCopied] = useState(false);

  const supportEmail = branding?.support_email || null;
  const supportUrl = branding?.support_url || null;
  const isPlatformOperator = user?.isSuperadmin
    || ['support_engineer', 'billing_operator', 'compliance_officer',
        'integration_engineer', 'read_only_auditor'].includes(user?.role);

  const copyDiagnostics = async () => {
    const text = diagnostics.map(([k, v]) => `${k}: ${v}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Details copied — paste them into your ticket');
    } catch {
      // Clipboard is blocked on insecure origins and in some embedded views.
      // The table is on screen either way, so this is a downgrade, not a dead end.
      toast.error('Could not copy. Select the details above and copy manually.');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Card>
        <CardHeader className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base">
            <LifeBuoy className="h-4 w-4 text-muted-foreground" />
            Support
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            How to get help with {appName || 'WebHR'}, and what to send so it can be answered first time.
          </p>
        </CardHeader>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base">
              <Ticket className="h-4 w-4 text-muted-foreground" />
              Raise a ticket
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              The tracked route. Your ticket stays attached to your workspace, so whoever picks it up
              already has the context.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Use this for anything specific to your organisation: a wrong leave balance, a missing
              payslip, an approval that went to the wrong person, access you need and do not have.
            </p>
            {onNavigate && (
              <Button size="sm" onClick={() => onNavigate('helpdesk')}>
                <Ticket className="mr-1.5 h-4 w-4" />
                Open the helpdesk
              </Button>
            )}
            {!onNavigate && (
              <p className="text-sm">
                Go to <span className="font-medium">Support → Helpdesk</span> in the sidebar.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base">
              <BookOpen className="h-4 w-4 text-muted-foreground" />
              Answer it yourself
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Help &amp; FAQs is written for your role and your industry, so it is shorter and more
              relevant than a general manual.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Most questions about applying for leave, fixing an attendance record, reading a payslip or
              changing your own details are answered there.
            </p>
            {onNavigate ? (
              <Button size="sm" variant="outline" onClick={() => onNavigate('help')}>
                <BookOpen className="mr-1.5 h-4 w-4" />
                Browse Help &amp; FAQs
              </Button>
            ) : (
              <p className="text-sm">
                Go to <span className="font-medium">Support → Help &amp; FAQs</span> in the sidebar.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {(supportEmail || supportUrl) && (
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="h-4 w-4 text-muted-foreground" />
              Contact
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Set by your organisation. Use these when you cannot sign in at all — a ticket needs a
              session, and this does not.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {supportEmail && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <a className="text-primary hover:underline" href={`mailto:${supportEmail}`}>{supportEmail}</a>
              </div>
            )}
            {supportUrl && (
              <div className="flex items-center gap-2 text-sm">
                <ExternalLink className="h-4 w-4 text-muted-foreground" />
                <a className="text-primary hover:underline" href={supportUrl} target="_blank" rel="noreferrer">
                  {supportUrl}
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
            What to include
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            A ticket with these four things is usually answered once. Without them it becomes a
            conversation.
          </p>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li><span className="font-medium text-foreground">What you expected</span> — and what happened instead.</li>
            <li><span className="font-medium text-foreground">When</span> — the date and roughly the time. Attendance and payroll questions are almost always about a specific day.</li>
            <li><span className="font-medium text-foreground">Who it affects</span> — just you, your team, or everyone.</li>
            <li><span className="font-medium text-foreground">The steps</span> — what you clicked, in order. A screenshot covers this in one go.</li>
          </ul>
          <p className="mt-4 text-sm text-muted-foreground">
            Never include your password, and never include a two-factor code. Nobody supporting this
            product will ask you for either.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldAlert className="h-4 w-4 text-muted-foreground" />
              Your session details
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Paste this into your ticket. It saves the first two replies being questions.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={copyDiagnostics}>
            {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
            {copied ? 'Copied' : 'Copy details'}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-border bg-muted/30 px-3 py-1">
            {diagnostics.map(([label, value]) => (
              <Row key={label} label={label} value={value} />
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            This is what your browser and your session already know. It contains no password and no
            authentication token.
          </p>
        </CardContent>
      </Card>

      {isPlatformOperator && (
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldAlert className="h-4 w-4 text-muted-foreground" />
              Operating the platform
              <Badge variant="outline" className="ml-1">Platform</Badge>
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              You are signed in with a platform role, so a workspace's ticket queue is not where your
              own questions go.
            </p>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              <span className="font-medium text-foreground">Before changing a governance baseline</span>,
              read what it covers — several of them include your own account, and take effect at your
              next sign-in rather than immediately.
            </p>
            <p>
              <span className="font-medium text-foreground">When a workspace reports something you cannot reproduce</span>,
              a proxy session shows you their view. It is recorded against your account, so end it when
              you are done.
            </p>
            <p>
              <span className="font-medium text-foreground">When mail is not arriving anywhere</span>,
              the cause is usually a missing email provider or sender identity rather than a broken
              template — nothing is queued at all in that state.
            </p>
            <p>Help &amp; FAQs carries a section written for platform roles, including yours.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SupportCenter;
